// Pure format-scoring for the isolated G-Formats sandbox: Stableford (standard
// + modified/configurable), Skins (carryover + next-hole validation), and
// Scramble (USGA/GG allowances via a GG-LOCAL table so the live app is never
// touched). Composes from a tournament-shaped object via node-safe scoring
// helpers. No React, no Firebase, no DB — unit-testable under `node --test`.
import { parsFor, strokeIndexFor, netScoresFor, teamGross, totalPar } from '../../lib/scoring.js'

const teamList = (sample) => {
  const out = []
  for (const [flightId, flight] of Object.entries(sample?.flights || {})) {
    for (const [teamId, t] of Object.entries(flight?.teams || {})) {
      out.push({ teamId, flightId, flightName: flight?.info?.name || flightId, info: t?.info || {}, scores: t?.scores || {} })
    }
  }
  return out
}
const scoresFor = (t, net, strokeIndex) => (net ? netScoresFor({ id: t.teamId, info: t.info, scores: t.scores }, strokeIndex) : t.scores)

// ── Stableford ─────────────────────────────────────────────────────────────
export const STABLEFORD_MODIFIED_DEFAULT = { albatross: 8, eagle: 5, birdie: 2, par: 0, bogey: -1, double: -3 }

// Points for a hole's score vs par. Standard: max(0, 2 − vsPar). Modified:
// configurable point values (albatross/eagle/birdie/par/bogey/double+).
export function stablefordPoints(vsPar, cfg = { mode: 'standard' }) {
  if (!cfg || cfg.mode !== 'modified') return Math.max(0, 2 - vsPar)
  const p = { ...STABLEFORD_MODIFIED_DEFAULT, ...(cfg.points || {}) }
  if (vsPar <= -3) return p.albatross
  if (vsPar === -2) return p.eagle
  if (vsPar === -1) return p.birdie
  if (vsPar === 0) return p.par
  if (vsPar === 1) return p.bogey
  return p.double
}

export function stablefordStandings(sample, { net = false, cfg = { mode: 'standard' } } = {}) {
  const pars = parsFor(sample)
  const strokeIndex = strokeIndexFor(sample)
  const rows = teamList(sample).map((t) => {
    const scores = scoresFor(t, net, strokeIndex)
    let points = 0
    for (let h = 1; h <= 18; h++) {
      const s = scores[h]
      if (s && Number.isFinite(s.bestBall)) points += stablefordPoints(s.bestBall - (pars[h - 1] || 4), cfg)
    }
    return { teamId: t.teamId, teamName: t.info.name || t.teamId, players: t.info.players || [], flightName: t.flightName, points }
  })
  rows.sort((a, b) => b.points - a.points)
  return rows.map((r, i) => ({ ...r, place: i + 1 }))
}

// ── Skins ──────────────────────────────────────────────────────────────────
export function computeSkins(sample, { net = false, carryover = true, validation = false, potPerHole = 0 } = {}) {
  const pars = parsFor(sample)
  const strokeIndex = strokeIndexFor(sample)
  const teams = teamList(sample).map((t) => ({ teamId: t.teamId, teamName: t.info.name || t.teamId, scores: scoresFor(t, net, strokeIndex) }))

  // Raw per-hole winner (strictly-lowest best ball; tie → null).
  const raw = []
  for (let h = 1; h <= 18; h++) {
    let best = Infinity
    let winners = []
    for (const t of teams) {
      const s = t.scores?.[h]?.bestBall
      if (!Number.isFinite(s)) continue
      if (s < best) { best = s; winners = [t.teamId] }
      else if (s === best) winners.push(t.teamId)
    }
    raw[h] = winners.length === 1 ? winners[0] : null
  }

  // Validation: a hole's winner keeps it only if that same team scores
  // par-or-better on the NEXT hole. Hole 18 has no next hole → auto-valid.
  const validOf = (h) => {
    if (!validation || raw[h] == null) return raw[h] != null
    if (h === 18) return true
    const wt = teams.find((t) => t.teamId === raw[h])
    const nextScore = wt?.scores?.[h + 1]?.bestBall
    return Number.isFinite(nextScore) && nextScore <= (pars[h] || 4) // pars[h] = par of hole h+1
  }

  const skinsByTeam = Object.fromEntries(teams.map((t) => [t.teamId, 0]))
  const holes = []
  let carriedIn = 0
  for (let h = 1; h <= 18; h++) {
    const skinsAtStake = 1 + carriedIn
    const winner = raw[h]
    const won = winner != null && validOf(h)
    const voided = winner != null && validation && !validOf(h)
    if (won) {
      skinsByTeam[winner] += skinsAtStake
      holes.push({ hole: h, winnerTeamId: winner, winnerName: teams.find((t) => t.teamId === winner)?.teamName || null, skinsAtStake, voided: false })
      carriedIn = 0
    } else {
      holes.push({ hole: h, winnerTeamId: null, winnerName: null, skinsAtStake, voided })
      carriedIn = carryover ? skinsAtStake : 0
    }
  }

  const teamsOut = teams
    .map((t) => ({ teamId: t.teamId, teamName: t.teamName, skins: skinsByTeam[t.teamId], value: skinsByTeam[t.teamId] * (Number(potPerHole) || 0) }))
    .sort((a, b) => b.skins - a.skins)
  return { holes, teams: teamsOut, carriedAtEnd: carriedIn }
}

// ── Scramble (GG-LOCAL allowance table; live scoring.js untouched) ──────────
export const GG_SCRAMBLE_WEIGHTS = { 1: [1.00], 2: [0.35, 0.15], 3: [0.30, 0.20, 0.10], 4: [0.25, 0.20, 0.15, 0.10] }

export function ggScrambleStrokes(handicaps) {
  const hcps = (handicaps || []).map(Number).filter(Number.isFinite)
  if (!hcps.length) return 0
  const w = GG_SCRAMBLE_WEIGHTS[hcps.length] || GG_SCRAMBLE_WEIGHTS[4]
  const sorted = [...hcps].sort((a, b) => a - b)
  let total = 0
  for (let i = 0; i < sorted.length; i++) total += sorted[i] * (w[i] || 0)
  return Math.round(total)
}

export function scrambleStandings(sample, { net = false } = {}) {
  const par = totalPar(parsFor(sample))
  const rows = teamList(sample).map((t) => {
    const teamScore = teamGross(t.scores)
    const strokes = ggScrambleStrokes(t.info.handicaps)
    return { teamId: t.teamId, teamName: t.info.name || t.teamId, players: t.info.players || [], flightName: t.flightName, teamScore, vsPar: teamScore - par, strokes, netScore: teamScore - strokes }
  })
  rows.sort((a, b) => (net ? a.netScore - b.netScore : a.teamScore - b.teamScore))
  return rows.map((r, i) => ({ ...r, place: i + 1 }))
}

// ── CSV exports ─────────────────────────────────────────────────────────────
const csv = (s) => (s == null ? '' : `"${String(s).replace(/"/g, '""')}"`)

export function stablefordCsv(rows) {
  const lines = ['Place,Team,Players,Points']
  rows.forEach((r) => lines.push([r.place, csv(r.teamName), csv((r.players || []).join(' / ')), r.points].join(',')))
  return lines.join('\n')
}

export function skinsCsv(skins) {
  const lines = ['Team,Skins,Value']
  ;(skins?.teams || []).forEach((t) => lines.push([csv(t.teamName), t.skins, t.value].join(',')))
  return lines.join('\n')
}

export function scrambleCsv(rows) {
  const lines = ['Place,Team,Players,Team Score,Vs Par,Net']
  rows.forEach((r) => lines.push([r.place, csv(r.teamName), csv((r.players || []).join(' / ')), r.teamScore, r.vsPar, r.netScore].join(',')))
  return lines.join('\n')
}
